<template>
  <table class="notice-table">
    <thead>
      <tr>
        <th class="column-number">번호</th>
        <th class="column-title">제목</th>
        <th class="column-author">작성자</th>
        <th class="column-date">작성일</th>
        <th v-if="admin" class="column-actions">관리</th>
      </tr>
    </thead>

    <tbody>
      <tr v-if="!displayed || displayed.length === 0">
        <td :colspan="admin ? 5 : 4">공지사항이 없습니다.</td>
      </tr>

      <tr v-for="(notice, index) in displayed"
          :key="notice.id ?? notice.uid ?? index"
          :class="{ pinned: isPinned(notice) }">
        <td class="column-number" :title="isPinned(notice) ? '상단고정' : ''">
          <span v-if="isPinned(notice)">공지</span>
          <span v-else>{{ rowNumber(index) }}</span>
        </td>

        <td class="column-title">
          <router-link :to="{ name: 'NoticeDetail', params: { id: notice.id ?? notice.uid } }">
            <span v-if="isPinned(notice)">📌 </span>
            {{ notice.title }}
            <span v-if="isNew(notice.createdAt)">🆕</span>
          </router-link>
        </td>

        <td class="column-author">관리자</td>
        <td class="column-date">{{ formatDate(notice.createdAt) }}</td>

        <td v-if="admin" class="column-actions">
          <button class="edit-delete-button" @click="$emit('edit', notice)">수정</button>
          <button class="edit-delete-button" @click="$emit('delete', notice)">삭제</button>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { reactive, ref, computed, onMounted, watch } from "vue";

const props = defineProps({
  /** 현재 페이지 데이터(클라 페이징된 조각) */
  notices: { type: Array, required: true },
  /** 전체 데이터(클라 페이징 전, 부모 보유 목록) -> 넘기면 API 스캔 끔 */
  allNotices: { type: Array, default: null },

  admin: { type: Boolean, default: false },
  currentPage: { type: Number, required: true },
  perPage: { type: Number, required: true },
  total: { type: Number, required: true },

  /** ↓ API 스캔 모드용 (allNotices 없을 때만 사용) */
  apiBase: { type: String, default: "/api/notices" }, // GET ?page=&size=
  scanPageSize: { type: Number, default: 100 },
  maxScanPages: { type: Number, default: 100 },
  eagerScan: { type: Boolean, default: true },
  pageParamBase: { type: Number, default: 0 },
  extraQuery: { type: Object, default: () => ({}) },
});

/** 전역 pinned 캐시 (id/uid → notice) */
const pinnedMap = reactive(new Map());
const pinnedLoaded = ref(false);
const scanning = ref(false);

function keyOf(n) { return n?.id ?? n?.uid; }
function isPinned(n) { return !!(n?.topFixed === true && pinnedMap.has(keyOf(n))); }

/** --- 모드 A: 부모가 전체 목록을 제공(allNotices) → 여기서 즉시 전역 pinned 생성 --- */
watch(() => props.allNotices, (list) => {
  if (!Array.isArray(list) || list.length === 0) return;
  pinnedMap.clear();
  for (const n of list) {
    if (n?.topFixed === true) {
      const k = keyOf(n);
      if (k != null) pinnedMap.set(k, n);
    }
  }
  pinnedLoaded.value = true; // API 스캔 불필요
}, { immediate: true });

/** --- 모드 B: allNotices가 없으면 API 스캔 로직 사용(기존 방식) --- */
async function preloadPinnedAcrossPages() {
  if (pinnedLoaded.value || scanning.value || Array.isArray(props.allNotices)) return; // allNotices 있으면 skip
  scanning.value = true;
  try {
    const size = props.scanPageSize;
    let page = props.pageParamBase;
    for (let i = 0; i < props.maxScanPages; i++) {
      const url = new URL(props.apiBase, window.location.origin);
      url.searchParams.set("page", String(page));
      url.searchParams.set("size", String(size));
      Object.entries(props.extraQuery || {}).forEach(([k, v]) => url.searchParams.set(k, String(v)));

      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const content = Array.isArray(data?.content) ? data.content : [];

      for (const n of content) {
        if (n?.topFixed === true) {
          const k = keyOf(n);
          if (k != null) pinnedMap.set(k, n);
        }
      }
      if (data?.last === true || content.length < size) break;
      page++;
    }
    pinnedLoaded.value = true;
  } catch (e) {
    console.warn("[pinned scan failed]", e);
  } finally {
    scanning.value = false;
  }
}

/** 현재 페이지 안의 pinned도 즉시 동기화(두 번 보이지 않도록) */
watch(() => props.notices, (nv) => {
  if (!Array.isArray(nv)) return;
  for (const n of nv) {
    if (n?.topFixed === true) {
      const k = keyOf(n);
      if (k != null && !pinnedMap.has(k)) pinnedMap.set(k, n);
    }
  }
}, { immediate: true });

onMounted(async () => {
  if (!Array.isArray(props.allNotices) && props.eagerScan) {
    await preloadPinnedAcrossPages();
  }
});

/** 렌더용 목록 */
const pinnedList = computed(() =>
  Array.from(pinnedMap.values()).sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0))
);
const nonPinnedForPage = computed(() => {
  const current = Array.isArray(props.notices) ? props.notices : [];
  return current
    .filter(n => !(n?.topFixed === true && pinnedMap.has(keyOf(n))))
    .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0));
});
const displayed = computed(() => [...pinnedList.value, ...nonPinnedForPage.value]);

/** 번호: pinned는 "-", 일반은 기존 수식 유지 */
function rowNumber(indexInDisplayed) {
  const n = displayed.value[indexInDisplayed];
  if (isPinned(n)) return "-";
  const idxInNonPinned = indexInDisplayed - pinnedList.value.length;
  return props.total - ((props.currentPage - 1) * props.perPage + idxInNonPinned);
}

/** 포맷 & NEW */
function formatDate(s) {
  const d = new Date(s ?? "");
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function isNew(date) {
  const posted = new Date(date ?? "");
  if (Number.isNaN(posted.getTime())) return false;
  const diffDays = Math.ceil(Math.abs(Date.now() - posted.getTime()) / 86400000);
  return diffDays <= 3;
}

defineExpose({ preloadPinnedAcrossPages: preloadPinnedAcrossPages });
</script>

<style scoped>
.notice-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; table-layout: fixed; text-align: center; }
.notice-table th, .notice-table td { padding: 12px; border-bottom: 1px solid #eee; white-space: normal; overflow-wrap: break-word; word-wrap: break-word; overflow: hidden; text-align: center !important; vertical-align: middle; }
.notice-table th { background-color: #f6f6f6; text-align: center; }
.notice-table td.column-title { text-align: left !important; padding-left: 15px; overflow: hidden; text-overflow: ellipsis; }
.column-number { width: 60px; max-width: 60px; text-align: center; }
.column-author { width: 100px; max-width: 100px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: center; }
.column-title { width: 100%; min-width: 60%; overflow: hidden; text-overflow: ellipsis; padding-left: 15px; text-align: left !important; }
.column-date { width: 120px; max-width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: center; }
.column-actions { width: 120px; max-width: 120px; text-align: center; }
.notice-table tr.pinned { background-color: rgba(255,230,230,.8); font-weight: bold; }
.notice-table tr.pinned:hover { background-color: rgba(255,200,200,.9); }
.notice-table a { text-decoration: none; color: #333; display: block; width: 100%; overflow: hidden; text-overflow: ellipsis; }
.notice-table a:hover { text-decoration: underline; }
.actions { display: flex; gap: 8px; justify-content: center; flex-wrap: nowrap; }
.edit-delete-button { padding: 6px 10px; font-size: .85rem; background-color: #007bff; color: #fff; border: none; border-radius: 4px; cursor: pointer; white-space: nowrap; }
.edit-delete-button:hover { background-color: #0056b3; }
@media (max-width: 768px){ .notice-table{font-size:.85rem} .notice-table th,.notice-table td{padding:8px 4px} .column-number{width:50px;max-width:50px} .column-author{width:60px;max-width:60px} .column-date{width:70px;max-width:70px} .column-title{min-width:50%} .column-actions{width:100px;max-width:100px} .edit-delete-button{font-size:.75rem;padding:4px 6px} }
@media (max-width: 480px){ .column-number{width:50px;max-width:50px} .column-author{width:50px;max-width:50px} .column-date{width:60px;max-width:60px} }
</style>
